import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ItemDetails } from 'components/ItemDetails';
import { MenuProps } from 'components/Menu';
import { ModalScreens, Screens } from 'components/Navigation/types';
import { CloudThumbnail } from 'components/Thumbnail/CloudThumbnail';
import { LocalThumbnail } from 'components/Thumbnail/LocalThumbnail';
import { Txt } from 'components/Txt';
import equal from 'fast-deep-equal';
import { useSelector } from 'hooks/useSelector';
import { getOptionFieldResultDisplayValues, hasValue } from 'lib/fields';
import { pluralise } from 'lib/pluralise';
import {
  filterArrayEmpty,
  rbAddReferencePrefix,
  stringRemoveNewLines,
} from 'lib/riskbase';
import { theme } from 'lib/theme';
import { isEqual } from 'lodash';
import React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import { APIRemoteUrl } from 'service/api/constants';
import {
  doAseFieldInstanceAddExistingModal,
  doAseFieldInstanceAddManyExisting,
  doAseFieldInstanceDelete,
  doAseFieldInstanceDuplicate,
  doAseFieldInstanceRelink,
  doAseFieldInstanceUnlink,
  doAseLoadElementPdf,
  doAseOpenFieldInstance,
} from 'store/assessments/actions';
import {
  selectAseFieldInstanceAseMerged,
  selectAseFieldInstanceAst,
  selectAseTplFieldGroup,
  selectAseIsEditable,
  selectAseTemplateFieldFieldsConfig,
  selectAseSectionActionIds,
  selectAseSectionControlIds,
  selectAseTemplateFieldGroupConfig,
  selectAseSectionUnreviewedActionIds,
  selectAseSectionUnreviewedControlIds,
  selectAseTplFieldToGroup,
  selectAseFieldInstanceChildInstanceIsInParentAstAse,
  selectAseFieldInstanceChildInstanceIsInParentAst,
  selectAseExplorerOptionCollapseFinding,
  selectAseFieldInstanceRequiredFieldValues,
  selectAseDataFieldFields,
} from 'store/assessments/selectors';
import { InstanceParentParams } from 'store/assessments/types';
import { FieldTypes } from 'store/assets/types';
import { useNavigationIsSelectedMulti } from 'hooks/useNavigationIsSelected';
import { NAVIGATE_MODAL } from 'store/actions';
import { str } from 'lib/str';
import { ExplorerActionRow } from '../Explorer/ExplorerActionRow';
import { ExplorerControlRow } from '../Explorer/ExplorerControlRow';
import { ExplorerQuestion } from '../Explorer/ExplorerQuestion';
import { ExplorerInstanceCheckRowPreAdd } from '../Explorer/ExplorerInstanceCheckRowPreAdd';
import { ExplorerInstanceCheckRow } from '../Explorer/ExplorerInstanceCheckRow';
import { CountChips } from '../CountChips';

type Props = {
  instanceId: string;
  checkedValue?: boolean;
  remoteImageUrl?: APIRemoteUrl;
  parent: InstanceParentParams;
  onPress?: (instanceId: string) => void;
} & (
  | {
      from: 'list' | 'inInstance';
      disableAddButton?: false | undefined;
    }
  | {
      from: 'add';
      disableAddButton: boolean;
    }
);

const BoundEllipsisText: React.FC<{
  topText: string;
  bottomText: string;
}> = ({ topText, bottomText }) => {
  const [width, setWidth] = React.useState(0);
  return (
    <View>
      <Txt
        size="xxxsmall"
        numberOfLines={1}
        style={{ maxWidth: width, minWidth: width, marginBottom: -3 }}
      >
        {topText}
      </Txt>
      <Txt
        weight="medium"
        numberOfLines={1}
        onLayout={(event) =>
          setWidth(Math.max(event.nativeEvent.layout.width, 12))
        }
        style={{ alignSelf: 'flex-start' }}
      >
        {bottomText}
      </Txt>
    </View>
  );
};

export const RepeatableListItem: React.FC<Props> = React.memo(
  ({
    from,
    instanceId,
    parent,
    checkedValue,
    remoteImageUrl,
    onPress,
    disableAddButton,
  }) => {
    const dispatch = useDispatch();
    const collapseFindings = useSelector(
      selectAseExplorerOptionCollapseFinding,
    );

    const isAddScreen = from === 'add';
    const isInInstanceScreen = from === 'inInstance';

    const assessmentInstance = useSelector(
      (state) => selectAseFieldInstanceAseMerged(state, instanceId),
      equal,
    );

    const assetInstance = useSelector(
      (state) => selectAseFieldInstanceAst(state, instanceId),
      equal,
    );

    const inAssessment = !!assessmentInstance;

    const groupId = assessmentInstance?.groupId || assetInstance?.groupId;

    const actionIds = useSelector(
      (state) => selectAseSectionActionIds(state, '1', instanceId),
      isEqual,
    );
    const controlIds = useSelector(
      (state) => selectAseSectionControlIds(state, '1', instanceId),
      isEqual,
    );
    const unreviewedActionIds = useSelector(
      (state) => selectAseSectionUnreviewedActionIds(state, '1', instanceId),
      isEqual,
    );
    const unreviewedControlIds = useSelector(
      (state) => selectAseSectionUnreviewedControlIds(state, '1', instanceId),
      isEqual,
    );

    const isEditable = useSelector(selectAseIsEditable);

    const group = useSelector((state) =>
      selectAseTplFieldGroup(state, groupId || '-1'),
    );
    const fields = useSelector(selectAseDataFieldFields);
    const parentGroup = useSelector((state) =>
      selectAseTplFieldToGroup(state, parent?.fieldId || '-1'),
    );

    const isInParentAstAse = useSelector((state) =>
      selectAseFieldInstanceChildInstanceIsInParentAstAse(
        state,
        parent,
        instanceId,
      ),
    );

    const isInParentAst = useSelector((state) =>
      selectAseFieldInstanceChildInstanceIsInParentAst(
        state,
        parent,
        instanceId,
      ),
    );

    const groupConfig = useSelector((state) =>
      selectAseTemplateFieldGroupConfig(state, groupId || '-1'),
    );
    const fieldsConfig = useSelector(selectAseTemplateFieldFieldsConfig);
    const isActiveInList = inAssessment && (!parentGroup || isInParentAstAse);

    const selected = useNavigationIsSelectedMulti(
      collapseFindings
        ? [
            {
              name: Screens.ASEREPEATABLE_INSTANCE,
              params: {
                instanceId,
                parent,
              },
            },
            ...[...unreviewedActionIds, ...actionIds].map((actionId) => ({
              name: Screens.ASEREPEATABLE_ACTION,
              params: { id: actionId, parent },
            })),
            ...[...unreviewedControlIds, ...controlIds].map((controlId) => ({
              name: Screens.ASEREPEATABLE_CONTROL,
              params: { id: controlId, parent },
            })),
          ]
        : [],
    );

    const instance = assessmentInstance || assetInstance;

    const { imageFieldId, fieldIds, disableAutoId, qrFieldId, pinFieldId } =
      React.useMemo(
        () => ({
          imageFieldId: group?.calculatedInfo?.imageFieldId,
          qrFieldId: group?.calculatedInfo?.qrFieldId,
          pinFieldId: group?.calculatedInfo?.pinFieldId,
          fieldIds: group?.fieldIds || [],
          disableAutoId: group?.disableAutoId ?? true,
        }),
        [group],
      );

    const {
      instanceResults,
      instanceFormattedAutoId,
      instanceAutoId,
      instanceReference,
    } = React.useMemo(
      () => ({
        instanceResults: instance?.results || {},
        instanceFormattedAutoId: instance?.formattedAutoId || '',
        instanceAutoId: instance?.autoId || '',
        instanceReference: instance?.reference || '',
      }),
      [instance],
    );

    const imageId = imageFieldId
      ? instanceResults[imageFieldId]?.value?.[0]
      : undefined;

    const resultsCount = Object.values(instanceResults).filter(hasValue).length;

    const additionalTitles = React.useMemo(
      () =>
        fieldIds.reduce<{ key: string; label: string; value: string }[]>(
          (acc, fieldId) => {
            // Field must be included in fieldsConfig for the user to see it in this assessment
            if (fieldsConfig[fieldId]) {
              const field = fields[fieldId]!;
              const result = instanceResults[fieldId];
              if (result) {
                if (field.type === FieldTypes.OPTION) {
                  const optionValues = getOptionFieldResultDisplayValues(
                    field,
                    result,
                    true,
                  ).join(', ');
                  if (
                    optionValues &&
                    optionValues !== 'Yes' &&
                    optionValues !== 'No'
                  ) {
                    acc.push({
                      key: fieldId,
                      label: field.label,
                      value: optionValues,
                    });
                  }
                } else if (
                  field.type === FieldTypes.TEXT ||
                  field.type === FieldTypes.NOTE ||
                  field.type === FieldTypes.NUMBER
                ) {
                  if (result.value?.length) {
                    acc.push({
                      key: fieldId,
                      label: field.label,
                      value: stringRemoveNewLines(`${result.value}`),
                    });
                  }
                }
              }
            }
            return acc;
          },
          disableAutoId
            ? []
            : [{ key: 'autoId', label: 'ID', value: instanceFormattedAutoId }],
        ),
      [
        fieldIds,
        disableAutoId,
        instanceFormattedAutoId,
        instanceResults,
        fieldsConfig,
        fields,
      ],
    );

    const hasQr = qrFieldId && hasValue(instanceResults[qrFieldId]);
    const hasPin = pinFieldId && hasValue(instanceResults[pinFieldId]);
    const isParentUnlinked = !isAddScreen && parentGroup && !isInParentAstAse;
    const isParentLinked = !isAddScreen && parentGroup && !isInParentAst;
    const isInAssessment = !!assessmentInstance;

    const ItemTitle = React.useMemo(
      () => (
        <View
          style={{
            flex: 1,
            flexDirection: 'row',
            columnGap: theme.paddingHorizontal,
            overflow: 'hidden',
          }}
        >
          {additionalTitles.length ? (
            additionalTitles.map((title) => (
              <BoundEllipsisText
                key={title.key}
                topText={title.label}
                bottomText={title.value}
              />
            ))
          ) : (
            <Txt weight="medium">No Info</Txt>
          )}
        </View>
      ),
      [additionalTitles],
    );

    const requiredValuesCount = useSelector((state) =>
      selectAseFieldInstanceRequiredFieldValues(state, instanceId),
    );

    if (!instance || !group) {
      return null;
    }

    if (isInInstanceScreen) {
      return (
        <CardRow
          childrenStyle={{
            alignItems: 'center',
            flex: 1,
            flexDirection: 'row',
            justifyContent: 'center',
          }}
          contentStyle={{
            paddingTop: theme.paddingHorizontalHalf,
            paddingBottom: theme.paddingHorizontalHalf,
          }}
          actionIcon={ICONS.CircleInfoLight}
          onAction={() =>
            dispatch(
              NAVIGATE_MODAL({
                screen: ModalScreens.AseFieldInstance,
                params: {
                  instanceId,
                  groupId: group.id,
                  parent,
                  isEditable: false,
                },
              }),
            )
          }
          indentActions
        >
          {!!imageFieldId && (
            <View style={{ marginRight: theme.paddingHorizontal, width: 52 }}>
              {remoteImageUrl ? (
                <CloudThumbnail
                  imageUrl={remoteImageUrl(imageId)}
                  width={52}
                  height={52}
                  rounded
                />
              ) : (
                <LocalThumbnail
                  thumbnailId={imageId}
                  width={52}
                  height={52}
                  rounded
                />
              )}
            </View>
          )}

          <View
            style={{
              flex: 1,
              marginVertical: -1,
            }}
          >
            {ItemTitle}
            <ItemDetails
              title=""
              subtitle={
                <Txt size="xsmall">
                  {`${group.nameSingular} • `}
                  {instanceReference
                    ? `${rbAddReferencePrefix(instanceReference)}`
                    : ''}
                </Txt>
              }
            />
          </View>
        </CardRow>
      );
    }

    const menuOptions: MenuProps['options'] | undefined =
      isActiveInList && (isEditable || group.allowPdfDownload)
        ? [
            isEditable
              ? {
                  text: `Duplicate ${group.calculatedInfo.singularName}`,
                  onPress: () =>
                    dispatch(
                      doAseFieldInstanceDuplicate({
                        instanceId,
                        parent,
                      }),
                    ),
                  icon: ICONS.CopyLight,
                }
              : undefined,
            isEditable
              ? {
                  text: instanceAutoId
                    ? 'Remove From Assessment'
                    : 'Delete From RiskBase',
                  onPress: () =>
                    dispatch(
                      doAseFieldInstanceDelete({
                        instanceId,
                      }),
                    ),
                  icon: ICONS.TrashCanLight,
                  destructive: true,
                }
              : undefined,
            isEditable && parent && parentGroup
              ? {
                  text: `Unlink From ${parentGroup.calculatedInfo.singularName}`,
                  icon: ICONS.LinkHorizontalSlashLight,
                  onPress: () =>
                    dispatch(
                      doAseFieldInstanceUnlink({
                        instanceId,
                        parent,
                      }),
                    ),
                  destructive: true,
                }
              : undefined,
            '-',
            group.allowPdfDownload
              ? {
                  text: 'Preview Element PDF',
                  onPress: () =>
                    dispatch(
                      doAseLoadElementPdf({
                        elementId: instanceId,
                      }),
                    ),
                  icon: ICONS.FileDuotone,
                }
              : undefined,
            group.allowPdfDownload
              ? {
                  text: str.sharedownload('Element PDF'),
                  onPress: () =>
                    dispatch(
                      doAseLoadElementPdf({
                        elementId: instanceId,
                        doShare: true,
                      }),
                    ),
                  icon: ICONS.ShareOS,
                }
              : undefined,
          ].filter(filterArrayEmpty)
        : undefined;

    const handlePress = onPress
      ? () => onPress(instanceId)
      : isActiveInList
        ? () =>
            dispatch(
              doAseOpenFieldInstance({
                instanceId,
                parent,
                scrollTo: false,
              }),
            )
        : undefined;

    let handleAddPress: (() => void) | undefined | null;
    let addPressIcon: ICONS | undefined;
    if (isEditable) {
      if (isAddScreen) {
        if (!disableAddButton) {
          handleAddPress = () =>
            dispatch(
              doAseFieldInstanceAddExistingModal({
                instanceId,
                parent,
              }),
            );
          addPressIcon = ICONS.PlusLight;
        }
      } else if (!inAssessment && parentGroup && isInParentAstAse) {
        handleAddPress = () =>
          dispatch(
            doAseFieldInstanceAddManyExisting({
              instanceIds: [instanceId],
              parent,
            }),
          );
        addPressIcon = ICONS.PlusLight;
      }
    }

    let handleLinkagePress: (() => void) | undefined;
    let linkagePressIcon: ICONS | undefined;
    if (isEditable && !menuOptions) {
      if (!isAddScreen) {
        if (parent) {
          if (!isInParentAstAse) {
            handleLinkagePress = () =>
              dispatch(
                doAseFieldInstanceRelink({
                  instanceId,
                  parent,
                }),
              );
            linkagePressIcon = ICONS.LinkHorizontalLight;
          } else {
            linkagePressIcon = ICONS.LinkHorizontalSlashLight;
            handleLinkagePress = () =>
              dispatch(
                doAseFieldInstanceUnlink({
                  instanceId,
                  parent,
                }),
              );
          }
        }
      }
    }

    return (
      <Card
        separator={null}
        style={{
          marginBottom: theme.paddingVertical / 2,
        }}
        margin={false}
      >
        <CardRow
          onPress={handlePress}
          navigationIsSelected={{
            name: Screens.ASEREPEATABLE_INSTANCE,
            params: {
              instanceId,
              parent,
            },
          }}
          style={[
            { paddingLeft: 0, paddingRight: 0 },
            !inAssessment && !isAddScreen
              ? { backgroundColor: theme.colors.white50 }
              : undefined,
          ]}
          contentStyle={{ paddingTop: 0, paddingBottom: 0 }}
          hideMenuButton
          menuOptions={menuOptions}
        >
          <CardRow
            checkedValue={checkedValue}
            childrenStyle={{ flexDirection: 'row', alignItems: 'center' }}
            style={{ backgroundColor: 'transparent' }}
            indentActions
            actionIcon={addPressIcon}
            onAction={handleAddPress}
            actionIcon2={linkagePressIcon}
            onAction2={handleLinkagePress}
            menuOptions={menuOptions}
            showMenuOnContext={false}
          >
            <View
              style={{
                alignItems: 'center',
                flex: 1,
                flexDirection: 'row',
                justifyContent: 'center',
              }}
            >
              {!!imageFieldId && (
                <View
                  style={{ marginRight: theme.paddingHorizontal, width: 52 }}
                >
                  {remoteImageUrl ? (
                    <CloudThumbnail
                      imageUrl={remoteImageUrl(imageId)}
                      width={52}
                      height={52}
                      rounded
                    />
                  ) : (
                    <LocalThumbnail
                      thumbnailId={imageId}
                      width={52}
                      height={52}
                      rounded
                    />
                  )}
                </View>
              )}
              <View
                style={{
                  flex: 1,
                  marginVertical: -1,
                }}
              >
                {ItemTitle}
                <ItemDetails
                  title=""
                  subtitle={
                    <>
                      <Txt size="xsmall">
                        {instanceReference
                          ? `${rbAddReferencePrefix(instanceReference)} • `
                          : ''}
                        {pluralise(
                          resultsCount,
                          '1 Result',
                          '{x} Results',
                          'No Results',
                        )}
                        {!!requiredValuesCount && (
                          <Txt
                            size="xsmall"
                            style={{ color: theme.colors.red }}
                          >
                            {' '}
                            {requiredValuesCount} Required
                          </Txt>
                        )}
                      </Txt>
                      {!instanceAutoId && (
                        <>
                          <Txt size="xsmall"> • </Txt>
                          <Txt
                            size="xsmall"
                            style={{ color: theme.colors.green }}
                          >
                            New
                          </Txt>
                        </>
                      )}
                      {isParentUnlinked && (
                        <>
                          <Txt size="xsmall"> • </Txt>
                          <Txt
                            size="xsmall"
                            style={{ color: theme.colors.red }}
                          >
                            Unlinked in Assessment
                          </Txt>
                        </>
                      )}
                      {isParentLinked && (
                        <>
                          <Txt size="xsmall"> • </Txt>
                          <Txt
                            size="xsmall"
                            style={{ color: theme.colors.green }}
                          >
                            Linked in Assessment
                          </Txt>
                        </>
                      )}
                      {isAddScreen && isInAssessment && (
                        <>
                          <Txt size="xsmall"> • </Txt>
                          <Txt
                            size="xsmall"
                            style={{ color: theme.colors.green }}
                          >
                            In Assessment
                          </Txt>
                        </>
                      )}
                    </>
                  }
                  subtitleIcons={[
                    hasQr ? ICONS.QRCodeLight : null,
                    hasPin ? ICONS.LocationPinLight : null,
                  ].filter(filterArrayEmpty)}
                />
              </View>
              {isActiveInList &&
                !selected &&
                collapseFindings &&
                (!!actionIds.length ||
                  !!controlIds.length ||
                  !!unreviewedActionIds.length ||
                  !!unreviewedControlIds.length) && (
                  <CountChips
                    questions={0}
                    checklists={0}
                    checklistsCompleted={0}
                    questionsCompleted={0}
                    actions={actionIds.length}
                    controls={controlIds.length}
                    unreviewedActions={unreviewedActionIds.length}
                    unreviewedControls={unreviewedControlIds.length}
                    style={{ marginLeft: theme.paddingHorizontal }}
                  />
                )}
            </View>
          </CardRow>
          {isActiveInList &&
            (selected || !collapseFindings) &&
            (!!actionIds.length ||
              !!controlIds.length ||
              !!unreviewedActionIds.length ||
              !!unreviewedControlIds.length) && (
              <Card
                expanded
                margin={false}
                style={{
                  marginBottom: theme.paddingHorizontal,
                }}
                bodyStyle={{
                  borderColor: '#f1f1f1',
                }}
              >
                {actionIds.map((actionId) => (
                  <ExplorerActionRow
                    key={actionId}
                    id={actionId}
                    parent={parent}
                  />
                ))}
                {controlIds.map((controlId) => (
                  <ExplorerControlRow
                    key={controlId}
                    id={controlId}
                    parent={parent}
                  />
                ))}
                {unreviewedActionIds.map((actionId) => (
                  <ExplorerActionRow
                    key={`reviewaction-${actionId}`}
                    id={actionId}
                    parent={parent}
                  />
                ))}
                {unreviewedControlIds.map((controlId) => (
                  <ExplorerControlRow
                    key={`reviewcontrol-${controlId}`}
                    id={controlId}
                    parent={parent}
                  />
                ))}
              </Card>
            )}
        </CardRow>
        {isActiveInList &&
          groupConfig.checkIds.map((checkId) => (
            <ExplorerInstanceCheckRow
              key={`check-${checkId}`}
              id={checkId}
              instanceId={instanceId}
            />
          ))}
        {isActiveInList &&
          groupConfig.questionIds.map((questionId) => (
            <ExplorerQuestion
              id={questionId}
              fieldInstanceId={instanceId}
              key={`q-${questionId}`}
              parent={parent}
            />
          ))}
        {!isActiveInList &&
          groupConfig.checkIds.map((checkId) => (
            <ExplorerInstanceCheckRowPreAdd
              key={`check-${checkId}`}
              id={checkId}
              instanceId={instanceId}
              hideCompleted
            />
          ))}
      </Card>
    );
  },
);
